import { useQuery } from '@tanstack/react-query';

import type { UserPreferenceLocation } from '@cognite/apm-client';

import { useFDMServices } from '../../../providers/fdm-services/fdm-services.context';
import { useAppConfigContext } from '../../../providers/is-idm-provider/app-config-provider';
import { QueryKeys } from '../../../utils/queryKeys';
import { useSpaceReadAccess } from '../../access/hooks/use-space-read-access';
import { getIsNewConfigVersion } from '../utils/utils';

import { useAppConfigQuery } from './use-query/use-app-config-query';

export const useSelectableLocations = () => {
  const { assetService } = useFDMServices();
  const { data: appConfig, isLoading: isLoadingAppConfig } =
    useAppConfigQuery();
  const { isIdm } = useAppConfigContext();
  const isNewConfigVersion = getIsNewConfigVersion(appConfig);
  const { data: access, isLoading: isLoadingSpaceAccess } =
    useSpaceReadAccess();

  return useQuery({
    queryKey: [QueryKeys.SELECTABLE_LOCATIONS, access, isIdm],
    queryFn: async () => {
      if (isIdm) {
        return [];
      }

      const spaceIds = access?.readAccessGroups || [];
      let selectableLocationsExternalIds = [];

      const configuredRootLocations =
        appConfig?.featureConfiguration?.rootLocationConfigurations;
      const configuredRootLocationExternalIds = configuredRootLocations?.map(
        (rootLocation) => ({
          assetExternalId: rootLocation.assetExternalId,
          configExternalId: rootLocation.externalId,
        })
      );

      if (
        configuredRootLocationExternalIds === undefined ||
        configuredRootLocationExternalIds.length === 0
      ) {
        throw Error(
          'Could not find any configured root location assetExternalIds'
        );
      }

      if (access?.hasReadAllSpacesAccess) {
        selectableLocationsExternalIds = configuredRootLocationExternalIds;
      } else {
        selectableLocationsExternalIds = configuredRootLocations!.reduce(
          (
            acc: {
              assetExternalId: string;
              configExternalId: string | undefined;
            }[],
            rootLocation
          ) => {
            const matchAppDataInstance =
              rootLocation.appDataInstanceSpace &&
              spaceIds.includes(rootLocation.appDataInstanceSpace);

            const matchSourceDataInstance =
              rootLocation.sourceDataInstanceSpace &&
              spaceIds.includes(rootLocation.sourceDataInstanceSpace);

            if (matchAppDataInstance || matchSourceDataInstance) {
              return [
                ...acc,
                {
                  assetExternalId: rootLocation.assetExternalId,
                  configExternalId: rootLocation.externalId,
                },
              ];
            }
            return acc;
          },
          []
        );
        if (selectableLocationsExternalIds.length === 0)
          throw Error('locationAccess');
      }

      const locationAssets = await assetService.getAPMAssetByExternalIds(
        selectableLocationsExternalIds.map(
          ({ assetExternalId }) => assetExternalId
        )
      );

      const sortedLocationAssets = locationAssets.sort((a, b) =>
        (a.title || '').localeCompare(b.title || '')
      );

      const selectableLocations = selectableLocationsExternalIds.reduce(
        (acc, { configExternalId, assetExternalId }) => {
          const correspondingAsset = sortedLocationAssets.find(
            ({ externalId }) => externalId === assetExternalId
          );
          if (correspondingAsset) {
            acc.push({
              externalId: isNewConfigVersion
                ? configExternalId!
                : assetExternalId,
              assetExternalId: isNewConfigVersion ? assetExternalId : undefined,
              name: correspondingAsset.title || correspondingAsset.externalId,
            });
          }
          return acc;
        },
        [] as UserPreferenceLocation[]
      );

      return selectableLocations;
    },
    meta: { error: { captureError: true, level: 'fatal' } },
    enabled: !isLoadingAppConfig && !isLoadingSpaceAccess,
    retry: false,
    refetchOnReconnect: false,
  });
};
